import React from 'react';
import { useAppContext, PAGES, PageType } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useNotificationContext } from '../context/NotificationContext';
import { useImpersonation } from '../context/ImpersonationContext';
import '../styles/Sidebar.css';

import iconPage2 from '../assets/icons/icon_page2.png';
import iconPage3 from '../assets/icons/icon_page3.png';
import iconPage4 from '../assets/icons/icon_page4.png';

const ChatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="12" y2="12" />
  </svg>
);

const NotificationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const AnalyseDocIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <circle cx="11" cy="14" r="3" />
    <path d="M13.5 16.5L16 19" />
  </svg>
);

const EnrichirIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="M5 10l7-7 7 7" />
    <rect x="4" y="17" width="16" height="4" rx="1" />
  </svg>
);

const ImageIcon: React.FC<{ src: string; className?: string; alt: string }> = ({ src, className, alt }) => (
  <div
    className={className}
    title={alt}
    style={{
      maskImage: `url(${src})`,
      WebkitMaskImage: `url(${src})`,
      maskSize: 'contain',
      WebkitMaskSize: 'contain',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskPosition: 'center',
      WebkitMaskPosition: 'center',
      backgroundColor: 'currentColor',
    }}
  />
);

const getIcon = (iconType: string) => {
  switch (iconType) {
    case 'chat':
      return ChatIcon;
    case 'analyse-doc':
      return AnalyseDocIcon;
    case 'page2':
      return ({ className }: { className?: string }) => <ImageIcon src={iconPage2} className={className} alt="Settings" />;
    case 'page3':
      return ({ className }: { className?: string }) => <ImageIcon src={iconPage3} className={className} alt="Documents" />;
    case 'page4':
      return ({ className }: { className?: string }) => <ImageIcon src={iconPage4} className={className} alt="Profile" />;
    case 'enrichir':
      return EnrichirIcon;
    case 'notifications':
      return NotificationIcon;
    default:
      return ChatIcon;
  }
};

export const Sidebar: React.FC = () => {
  const { activePage, setActivePage } = useAppContext();
  const { authStatus, logout, microsoftDisplayName, microsoftPhotoUrl } = useAuth();
  const { unreadCount } = useNotificationContext();
  const { isImpersonating, activeAlias, clearImpersonation, canImpersonate } = useImpersonation();
  const [photoError, setPhotoError] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

  // Reset photo error when microsoftPhotoUrl changes so new images will be retried
  React.useEffect(() => {
    setPhotoError(false);
  }, [microsoftPhotoUrl]);
  const [menuPosition, setMenuPosition] = React.useState<{ left: number; bottom: number } | null>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const userButtonRef = React.useRef<HTMLButtonElement>(null);
  const sidebarRef = React.useRef<HTMLElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const closeTimeoutRef = React.useRef<number | null>(null);

  const handlePageClick = (pageId: PageType) => {
    setActivePage(pageId);
  };

  // Close user menu on outside click
  React.useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const user = authStatus?.user;
  const email = user?.email || '';
  const company = user?.company;
  const isAdmin = user?.is_admin || false;
  const initial = email.charAt(0).toUpperCase();

  const getDisplayName = (): string => {
    // Priority 1: Microsoft Graph displayName
    if (microsoftDisplayName && microsoftDisplayName.trim()) {
      return microsoftDisplayName;
    }
    // Fallback: Extract from email
    const emailName = email.split('@')[0];
    return emailName
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const displayName = getDisplayName();

  const getColorFromEmail = (emailStr: string): string => {
    const colors = [
      '#0078d4', '#107c10', '#d83b01', '#8764b8',
      '#00b4ff', '#e81123', '#008272',
    ];
    let hash = 0;
    for (let i = 0; i < emailStr.length; i++) {
      hash = emailStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const backgroundColor = getColorFromEmail(email);

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleManageAccount = () => {
    window.open('https://account.microsoft.com', '_blank', 'noopener,noreferrer');
    setIsUserMenuOpen(false);
  };

  const handleChangeCompany = () => {
    setIsUserMenuOpen(false);
    setActivePage('company-selection');
  };

  const handleExitImpersonation = () => {
    setIsUserMenuOpen(false);
    clearImpersonation();
    setActivePage('chat');
  };

  const handleUserButtonClick = () => {
    if (isUserMenuOpen) {
      setIsUserMenuOpen(false);
      setMenuPosition(null);
    } else {
      if (sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        setMenuPosition({
          left: sidebarRect.right + 8,
          bottom: viewportHeight - sidebarRect.bottom + 12
        });
      }
      setIsUserMenuOpen(true);
    }
  };

  const closeMenu = React.useCallback(() => {
    setIsUserMenuOpen(false);
    setMenuPosition(null);
  }, []);

  const scheduleClose = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      closeMenu();
      closeTimeoutRef.current = null;
    }, 150);
  }, [closeMenu]);

  const cancelClose = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleSidebarMouseLeave = () => {
    if (isUserMenuOpen) {
      scheduleClose();
    }
  };

  const handleSidebarMouseEnter = () => {
    if (isUserMenuOpen) {
      cancelClose();
    }
  };

  const handleMenuMouseLeave = () => {
    scheduleClose();
  };

  const handleMenuMouseEnter = () => {
    cancelClose();
  };

  return (
    <aside
      className="sidebar"
      ref={sidebarRef}
      onMouseLeave={handleSidebarMouseLeave}
      onMouseEnter={handleSidebarMouseEnter}
    >
      <div className="sidebar-header">
        <span className="sidebar-logo">Corpus</span>
      </div>

      <div className="sidebar-separator" />

      <nav className="sidebar-nav">
        {PAGES.map((page) => {
          const IconComponent = getIcon(page.icon);
          const isActive = activePage === page.id;
          const showBadge = page.id === 'notifications' && unreadCount > 0;

          return (
            <button
              key={page.id}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => handlePageClick(page.id)}
              title={page.name}
            >
              <div className="sidebar-nav-icon-wrapper">
                <IconComponent className="sidebar-nav-icon" />
                {showBadge && (
                  <span className="sidebar-notification-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="sidebar-nav-label">{page.name}</span>
            </button>
          );
        })}
      </nav>

      {isImpersonating && (
        <div className="sidebar-impersonation-banner">
          Mode: {activeAlias?.company_name}
        </div>
      )}

      {authStatus?.authenticated && user && (
        <div className="sidebar-user-section" ref={userMenuRef}>
          <button
            ref={userButtonRef}
            className="sidebar-user-button"
            onClick={handleUserButtonClick}
            title={email}
            aria-label={`Menu utilisateur pour ${email}`}
            aria-haspopup="true"
            aria-expanded={isUserMenuOpen}
          >
            <div className="sidebar-user-avatar">
              {microsoftPhotoUrl && !photoError ? (
                <img
                  src={microsoftPhotoUrl}
                  alt={email}
                  className="sidebar-user-avatar-image"
                  onError={() => setPhotoError(true)}
                />
              ) : (
                <div
                  className="sidebar-user-avatar-initial"
                  style={{ backgroundColor }}
                >
                  {initial}
                </div>
              )}
            </div>
            <span className="sidebar-user-name">{displayName}</span>
          </button>

          {isUserMenuOpen && menuPosition && (
            <div
              ref={menuRef}
              className="sidebar-user-menu"
              style={{
                left: menuPosition.left,
                bottom: menuPosition.bottom
              }}
              onMouseLeave={handleMenuMouseLeave}
              onMouseEnter={handleMenuMouseEnter}
            >
              <div className="sidebar-user-menu-header">
                <div className="sidebar-user-menu-avatar">
                  {microsoftPhotoUrl && !photoError ? (
                    <img
                      src={microsoftPhotoUrl}
                      alt={email}
                      className="sidebar-user-avatar-image"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <div
                      className="sidebar-user-avatar-initial"
                      style={{ backgroundColor }}
                    >
                      {initial}
                    </div>
                  )}
                </div>
                <div className="sidebar-user-menu-info">
                  <div className="sidebar-user-menu-name-row">
                    <span className="sidebar-user-menu-name">{displayName}</span>
                    <span className={`sidebar-user-menu-role ${isAdmin ? 'admin' : 'member'}`}>
                      {isAdmin ? 'Admin' : 'Membre'}
                    </span>
                  </div>
                  <div className="sidebar-user-menu-email">{email}</div>
                  {company && (
                    <div className="sidebar-user-menu-company">{company}</div>
                  )}
                </div>
              </div>

              <div className="sidebar-user-menu-divider" />

              <div className="sidebar-user-menu-actions">
                <button
                  className="sidebar-user-menu-action"
                  onClick={handleManageAccount}
                >
                  Gérer mon compte
                </button>
                {canImpersonate && (
                  <>
                    <button
                      className="sidebar-user-menu-action"
                      onClick={handleChangeCompany}
                    >
                      Changer d'entreprise
                    </button>
                    {isImpersonating && (
                      <button
                        className="sidebar-user-menu-action exit-impersonation"
                        onClick={handleExitImpersonation}
                      >
                        Quitter mode {activeAlias?.company_name}
                      </button>
                    )}
                  </>
                )}
                <button
                  className="sidebar-user-menu-action"
                  onClick={handleSignOut}
                >
                  Me déconnecter
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
